import { computed, ref } from 'vue';

export interface ValidationRule {
    validate: (value: any) => boolean;
    message: string;
}

export interface FieldErrors {
    [key: string]: string[];
}

/**
 * Composable for form validation with custom rules
 */
export function useFormValidation() {
    const errors = ref<FieldErrors>({});

    const clearErrors = () => {
        errors.value = {};
    };

    const setFieldError = (field: string, message: string) => {
        if (!errors.value[field]) {
            errors.value[field] = [];
        }
        errors.value[field].push(message);
    };

    const clearFieldError = (field: string) => {
        delete errors.value[field];
    };

    const hasError = (field: string) => !!errors.value[field]?.length;
    const getErrorMessage = (field: string) => errors.value[field]?.[0] || '';

    // Common validators
    const validators = {
        required: (value: any): boolean => {
            if (typeof value === 'string') return value.trim().length > 0;
            return !!value;
        },
        email: (value: string): boolean => {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return emailRegex.test(value);
        },
        phone: (value: string): boolean => {
            const phoneRegex = /^[\d\s\-\+\(\)]{7,}$/;
            return phoneRegex.test(value);
        },
        minLength:
            (min: number) =>
            (value: string): boolean =>
                value.length >= min,
        maxLength:
            (max: number) =>
            (value: string): boolean =>
                value.length <= max,
        date: (value: string): boolean => {
            return /^\d{4}-\d{2}-\d{2}$/.test(value);
        },
        time: (value: string): boolean => {
            return /^\d{2}:\d{2}$/.test(value);
        },
    };

    return {
        errors: computed(() => errors.value),
        clearErrors,
        setFieldError,
        clearFieldError,
        hasError,
        getErrorMessage,
        validators,
    };
}
